import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Header } from "../components/shared/Header";
import { BookingSummary } from "../components/shared/BookingSummary";
import { AppointmentConfirmation } from "../components/shared/AppointmentConfirmation";
import { Footer } from "../components/shared/Footer";
import { useBookingStore } from "../store/bookingStore";
import { apiEndpoints } from "../services/api";
import { BookingFormData, APPOINTMENT_TYPES, Appointment } from "../types";
import { CreditCard, AlertCircle, ArrowLeft } from "lucide-react";
import {
  validateEmail,
  validatePhoneNumber,
  validateName,
  validateCardNumber,
  validateExpiryMonth,
  validateExpiryYear,
  validateCVV,
  validateAppointmentType,
  validateMeetingType,
  ValidationResult,
} from "../lib/utils";

export const BookingPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    selectedSlot,
    selectedConsultant,
    session,
    appointment,
    setAppointment,
  } = useBookingStore();
  const [formData, setFormData] = useState<BookingFormData>({
    first_name: "",
    last_name: "",
    email: "",
    contact_no: "",
    appointment_type: "",
    meeting_type: "online",
    session_id: "",
    card_details: {
      card_number: "",
      exp_month: "",
      exp_year: "",
      cvv: "",
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [paymentError, setPaymentError] = useState<string>("");

  // Set session ID when component mounts
  useEffect(() => {
    console.log("session", session);
    if (session) {
      setFormData((prev) => ({ ...prev, session_id: session._id }));
    }
  }, [session]);

  // Redirect if no slot/consultant selected
  useEffect(() => {
    if (!selectedSlot || !selectedConsultant || !session) {
      navigate("/consultation");
    }
  }, [selectedSlot, selectedConsultant, session, navigate]);

  const validateField = (field: string, value: string): ValidationResult => {
    switch (field) {
      case "first_name":
        return validateName(value, "First name");
      case "last_name":
        return validateName(value, "Last name");
      case "email":
        return validateEmail(value);
      case "contact_no":
        return validatePhoneNumber(value);
      case "appointment_type":
        return validateAppointmentType(value);
      case "meeting_type":
        return validateMeetingType(value);
      case "card_number":
        return validateCardNumber(value);
      case "exp_month":
        return validateExpiryMonth(value);
      case "exp_year":
        return validateExpiryYear(value);
      case "cvv":
        return validateCVV(value);
      default:
        return { isValid: true };
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCardInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      card_details: { ...prev.card_details, [field]: value },
    }));

    // Clear error when user starts typing
    const cardFieldKey = `card_${field}`;
    if (errors[cardFieldKey]) {
      setErrors((prev) => ({ ...prev, [cardFieldKey]: "" }));
    }
  };

  const handleBlur = (field: string, value: string) => {
    const validation = validateField(field, value);
    if (!validation.isValid) {
      setErrors((prev) => ({ ...prev, [field]: validation.message || "" }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCardBlur = (field: string, value: string) => {
    const cardFieldKey = `card_${field}`;
    const validation = validateField(field, value);
    if (!validation.isValid) {
      setErrors((prev) => ({
        ...prev,
        [cardFieldKey]: validation.message || "",
      }));
    } else {
      setErrors((prev) => ({ ...prev, [cardFieldKey]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate all fields
    const fieldsToValidate = [
      { key: "first_name", value: formData.first_name },
      { key: "last_name", value: formData.last_name },
      { key: "email", value: formData.email },
      { key: "contact_no", value: formData.contact_no },
      { key: "appointment_type", value: formData.appointment_type },
      { key: "meeting_type", value: formData.meeting_type },
      { key: "card_number", value: formData.card_details.card_number },
      { key: "exp_month", value: formData.card_details.exp_month },
      { key: "exp_year", value: formData.card_details.exp_year },
      { key: "cvv", value: formData.card_details.cvv },
    ];

    fieldsToValidate.forEach(({ key, value }) => {
      const validation = validateField(key, value);
      if (!validation.isValid) {
        // Use card_ prefix for card fields
        const errorKey = [
          "card_number",
          "exp_month",
          "exp_year",
          "cvv",
        ].includes(key)
          ? `card_${key}`
          : key;
        newErrors[errorKey] = validation.message || "";
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSlot || !selectedConsultant || !session) {
      return;
    }

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Submit directly with card data
    setIsSubmitting(true);
    setPaymentError("");

    try {
      // Book the appointment with card details directly
      const bookingData = JSON.parse(
        JSON.stringify({
          ...formData,
          session_id: session!._id,
        })
      );

      //expiery year only send last 2 digits
      bookingData.card_details.exp_year =
        bookingData.card_details.exp_year.slice(-2);

      const response = await apiEndpoints.bookAppointment(bookingData);
      console.log("Booking successful:", response.data);

      // Store appointment data in the store
      setAppointment(response.data.data as Appointment);

      // The component will re-render and show the confirmation page
    } catch (error) {
      console.error("Error booking appointment:", error);
      setPaymentError(
        "There was an error processing your booking. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show appointment confirmation if booking was successful
  if (appointment && selectedSlot && selectedConsultant) {
    return (
      <AppointmentConfirmation
        appointment={appointment}
        slot={selectedSlot}
        consultant={selectedConsultant}
      />
    );
  }

  if (!selectedSlot || !selectedConsultant || !session) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header
          title="No Booking Data Found"
          subtitle="Please select a slot from the calendar"
        />
        <div className="flex items-center justify-center py-16 flex-1">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">No booking data found</h2>
            <p className="text-muted-foreground mb-4">
              Please go back to the calendar and select a slot
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header
        title="Complete Your Booking"
        subtitle="Please fill in your details to confirm your appointment"
      />

      <div className="container mx-auto px-4 py-8 flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <BookingSummary
              slot={selectedSlot}
              selectedConsultant={selectedConsultant}
            />
            
            {/* Back to Calendar Button */}
            <div className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/consultation')}
                className="w-full flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Calendar
              </Button>
            </div>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) =>
                          handleInputChange("first_name", e.target.value)
                        }
                        onBlur={(e) => handleBlur("first_name", e.target.value)}
                        className={
                          errors.first_name
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }
                        required
                      />
                      {errors.first_name && (
                        <div className="flex items-center gap-1 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          {errors.first_name}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) =>
                          handleInputChange("last_name", e.target.value)
                        }
                        onBlur={(e) => handleBlur("last_name", e.target.value)}
                        className={
                          errors.last_name
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }
                        required
                      />
                      {errors.last_name && (
                        <div className="flex items-center gap-1 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          {errors.last_name}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        onBlur={(e) => handleBlur("email", e.target.value)}
                        className={
                          errors.email
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }
                        required
                      />
                      {errors.email && (
                        <div className="flex items-center gap-1 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          {errors.email}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact_no">Phone Number *</Label>
                      <Input
                        id="contact_no"
                        type="tel"
                        value={formData.contact_no}
                        onChange={(e) =>
                          handleInputChange("contact_no", e.target.value)
                        }
                        onBlur={(e) => handleBlur("contact_no", e.target.value)}
                        className={
                          errors.contact_no
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }
                        required
                      />
                      {errors.contact_no && (
                        <div className="flex items-center gap-1 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          {errors.contact_no}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Appointment Type */}
                    <div className="space-y-2">
                      <Label htmlFor="appointment_type">
                        Appointment Type *
                      </Label>
                      <Select
                        value={formData.appointment_type}
                        onValueChange={(value) =>
                          handleInputChange("appointment_type", value)
                        }
                      >
                        <SelectTrigger
                          className={
                            errors.appointment_type
                              ? "border-red-500 focus-visible:ring-red-500"
                              : ""
                          }
                        >
                          <SelectValue placeholder="Select appointment type" />
                        </SelectTrigger>
                        <SelectContent>
                          {APPOINTMENT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.appointment_type && (
                        <div className="flex items-center gap-1 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          {errors.appointment_type}
                        </div>
                      )}
                    </div>

                    {/* Meeting Type */}
                    <div className="space-y-2">
                      <Label htmlFor="meeting_type">Meeting Type *</Label>
                      <Select
                        value={formData.meeting_type}
                        onValueChange={(value) =>
                          handleInputChange("meeting_type", value)
                        }
                      >
                        <SelectTrigger
                          className={
                            errors.meeting_type
                              ? "border-red-500 focus-visible:ring-red-500"
                              : ""
                          }
                        >
                          <SelectValue placeholder="Select meeting type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="online">
                            Online (Video Call)
                          </SelectItem>
                          <SelectItem value="offline">In-Person</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.meeting_type && (
                        <div className="flex items-center gap-1 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          {errors.meeting_type}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div className="border-t pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <CreditCard className="h-5 w-5" />
                      <h3 className="text-lg font-semibold">
                        Payment Information
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="card_number">Card Number *</Label>
                        <Input
                          id="card_number"
                          value={formData.card_details.card_number}
                          onChange={(e) =>
                            handleCardInputChange("card_number", e.target.value)
                          }
                          onBlur={(e) =>
                            handleCardBlur("card_number", e.target.value)
                          }
                          placeholder="1234 5678 9012 3456"
                          className={
                            errors.card_number
                              ? "border-red-500 focus-visible:ring-red-500"
                              : ""
                          }
                          required
                        />
                        {errors.card_number && (
                          <div className="flex items-center gap-1 text-sm text-red-600">
                            <AlertCircle className="h-4 w-4" />
                            {errors.card_number}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="exp_month">Exp Month *</Label>
                          <Input
                            id="exp_month"
                            value={formData.card_details.exp_month}
                            onChange={(e) =>
                              handleCardInputChange("exp_month", e.target.value)
                            }
                            onBlur={(e) =>
                              handleCardBlur("exp_month", e.target.value)
                            }
                            placeholder="MM"
                            maxLength={2}
                            className={
                              errors.card_exp_month
                                ? "border-red-500 focus-visible:ring-red-500"
                                : ""
                            }
                            required
                          />
                          {errors.card_exp_month && (
                            <div className="flex items-center gap-1 text-sm text-red-600">
                              <AlertCircle className="h-4 w-4" />
                              {errors.card_exp_month}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="exp_year">Exp Year *</Label>
                          <Input
                            id="exp_year"
                            value={formData.card_details.exp_year}
                            onChange={(e) =>
                              handleCardInputChange("exp_year", e.target.value)
                            }
                            onBlur={(e) =>
                              handleCardBlur("exp_year", e.target.value)
                            }
                            placeholder="YYYY"
                            maxLength={4}
                            className={
                              errors.card_exp_year
                                ? "border-red-500 focus-visible:ring-red-500"
                                : ""
                            }
                            required
                          />
                          {errors.card_exp_year && (
                            <div className="flex items-center gap-1 text-sm text-red-600">
                              <AlertCircle className="h-4 w-4" />
                              {errors.card_exp_year}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cvv">CVV *</Label>
                          <Input
                            id="cvv"
                            value={formData.card_details.cvv}
                            onChange={(e) =>
                              handleCardInputChange("cvv", e.target.value)
                            }
                            onBlur={(e) =>
                              handleCardBlur("cvv", e.target.value)
                            }
                            placeholder="123"
                            maxLength={4}
                            className={
                              errors.card_cvv
                                ? "border-red-500 focus-visible:ring-red-500"
                                : ""
                            }
                            required
                          />
                          {errors.card_cvv && (
                            <div className="flex items-center gap-1 text-sm text-red-600">
                              <AlertCircle className="h-4 w-4" />
                              {errors.card_cvv}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Pay Now -{" "}
                        {selectedSlot.pricing.price.toLocaleString("en-CA", {
                          style: "currency",
                          currency: "CAD",
                        })}
                      </>
                    )}
                  </Button>

                  {/* Payment Error Display */}
                  {paymentError && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        <span className="font-medium">Payment Error:</span>
                      </div>
                      <p className="mt-1 text-red-600">{paymentError}</p>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};
